// Portfolio Metrics — the monthly combo chart (bar + line, dual axis) with a
// period picker, per-property filter, metric A/B selectors and market-ADR /
// market-occupancy overlays. Lived on the Reservations page; it's a portfolio
// financial view, so it now mounts at the top of Money → Performance
// (extracted per owner feedback — "why would Portfolio Metrics be stuck in
// Reservations?").
//
// Self-contained: render(host) injects the card, fetches
// /api/reservations/chart-data (night-allocated monthly revenue/nights/count,
// version-deduped server-side), and manages its own Chart.js lifecycle.

import Chart from 'chart.js/auto';
import { Formatters } from '../lib/formatters';

type MetricKey = 'revenue' | 'nights' | 'reservations' | 'adr' | 'mgmt_fee' | 'owner_net' | 'projected' | 'stly';

const METRIC_LABELS: Record<MetricKey, string> = {
  revenue: 'Revenue',
  nights: 'Nights',
  reservations: 'Reservations',
  adr: 'Avg Daily Rate',
  mgmt_fee: 'Mgmt revenue (engine)',
  owner_net: 'Owner net (engine)',
  projected: 'Projected gross (AirROI)',
  stly: 'Booked by this date LY (STLY)',
};
// Engine/projection series are portfolio-level (the fee engine and AirROI
// caches aren't served per property per month) — disabled under a property
// filter rather than shown wrong (PRINCIPLES #4).
const PORTFOLIO_ONLY: MetricKey[] = ['mgmt_fee', 'owner_net', 'projected', 'stly'];
const CURRENCY = new Set<MetricKey>(['revenue', 'adr', 'mgmt_fee', 'owner_net', 'projected', 'stly']);
// Same-unit metrics share ONE axis — two independently auto-scaled axes make
// a 2x revenue lead over STLY look like a sliver (dual-axis distortion).
// Dual axes only for incommensurable pairs (dollars vs nights, totals vs ADR).
const SCALE_GROUP: Record<MetricKey, string> = {
  revenue: 'total$', mgmt_fee: 'total$', owner_net: 'total$', projected: 'total$', stly: 'total$',
  adr: 'adr$', nights: 'count', reservations: 'count',
};

export class PortfolioMetricsChart {
  private host: HTMLElement | null = null;
  private chart: Chart | null = null;
  private chartData: any[] = [];
  private market: { adr?: number | null; occupancy?: number | null } | null = null;
  private listings: { id: string; name: string }[] = [];
  private selectedListingId = '';
  private metricA: MetricKey = 'revenue';
  private metricB: MetricKey = 'projected';
  // 'YYYY-MM' → portfolio-level financial series for that month.
  private finByMonth: Map<string, { mgmt_fee: number | null; owner_net: number | null; projected: number | null; stly: number | null }> = new Map();
  private fromMonth: string;
  private toMonth: string;

  constructor() {
    const now = new Date();
    this.fromMonth = `${now.getFullYear() - 1}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    this.toMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  async render(host: HTMLElement): Promise<void> {
    this.host = host;
    host.innerHTML = `
      <style>
        .pmx-card { background:var(--card); border:1px solid var(--border); border-radius:var(--radius-lg); box-shadow:var(--shadow-card); margin-bottom:20px; }
        .pmx-header { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; padding:16px 18px 4px; }
        .pmx-title { font-size:15px; font-weight:700; color:var(--text); }
        .pmx-controls { display:flex; align-items:flex-end; gap:12px; flex-wrap:wrap; }
        .pmx-group { display:flex; flex-direction:column; gap:3px; }
        .pmx-label { font-size:10px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; }
        .pmx-select { padding:5px 8px; border:1px solid var(--border); border-radius:6px; font-size:12px; background:#fff; color:var(--text); }
        .pmx-body { height:340px; padding:8px 18px 16px; }
        @media (max-width:768px){ .pmx-body { height:280px; } }
      </style>
      <div class="pmx-card">
        <div class="pmx-header">
          <div class="pmx-title">Portfolio Metrics <span id="pmx-range" style="color:var(--text-muted); font-weight:400; font-size:12px;"></span></div>
          <div class="pmx-controls">
            <div class="pmx-group"><span class="pmx-label">Period</span>
              <div style="display:flex; align-items:center; gap:4px;">
                <input type="month" id="pmx-from" class="pmx-select" value="${this.fromMonth}">
                <span style="color:var(--text-muted); font-size:12px;">→</span>
                <input type="month" id="pmx-to" class="pmx-select" value="${this.toMonth}">
              </div>
            </div>
            <div class="pmx-group"><span class="pmx-label">Property</span>
              <select id="pmx-property" class="pmx-select" style="min-width:170px;"><option value="">All Properties</option></select>
            </div>
            <div class="pmx-group"><span class="pmx-label">Metric A</span>
              <select id="pmx-metric-a" class="pmx-select">${this.options(this.metricA)}</select>
            </div>
            <div class="pmx-group"><span class="pmx-label">Metric B</span>
              <select id="pmx-metric-b" class="pmx-select">${this.options(this.metricB)}</select>
            </div>
          </div>
        </div>
        <div class="pmx-body"><canvas id="pmx-canvas"></canvas></div>
      </div>
    `;
    this.bind();
    await this.load();
  }

  destroy(): void {
    if (this.chart) { this.chart.destroy(); this.chart = null; }
  }

  private options(selected: MetricKey): string {
    return (Object.keys(METRIC_LABELS) as MetricKey[])
      .map(k => {
        const off = this.selectedListingId && PORTFOLIO_ONLY.includes(k);
        return `<option value="${k}" ${k === selected ? 'selected' : ''} ${off ? 'disabled title="portfolio-level series — not available per property"' : ''}>${METRIC_LABELS[k]}</option>`;
      }).join('');
  }

  // Refresh the metric selects (disabled states change with the property filter).
  private refreshMetricSelects(): void {
    const a = document.getElementById('pmx-metric-a') as HTMLSelectElement | null;
    const b = document.getElementById('pmx-metric-b') as HTMLSelectElement | null;
    if (a) a.innerHTML = this.options(this.metricA);
    if (b) b.innerHTML = this.options(this.metricB);
  }

  private bind(): void {
    const on = (id: string, fn: (v: string) => void) =>
      document.getElementById(id)?.addEventListener('change', (e) => fn((e.target as HTMLInputElement).value));
    on('pmx-from', v => { if (v) { this.fromMonth = v; this.load(); } });
    on('pmx-to', v => { if (v) { this.toMonth = v; this.load(); } });
    on('pmx-property', v => {
      this.selectedListingId = v;
      // Portfolio-only series make no sense under a property filter — fall back.
      if (v && PORTFOLIO_ONLY.includes(this.metricA)) this.metricA = 'revenue';
      if (v && PORTFOLIO_ONLY.includes(this.metricB)) this.metricB = 'nights';
      this.refreshMetricSelects();
      this.load();
    });
    on('pmx-metric-a', v => { this.metricA = v as MetricKey; this.draw(); });
    on('pmx-metric-b', v => { this.metricB = v as MetricKey; this.draw(); });
  }

  private async load(): Promise<void> {
    try {
      const from = this.fromMonth + '-01';
      const [toY, toM] = this.toMonth.split('-').map(Number);
      const to = new Date(toY, toM, 0).toISOString().split('T')[0];
      const url = `/api/reservations/chart-data?from=${from}&to=${to}${this.selectedListingId ? `&listing_id=${this.selectedListingId}` : ''}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      this.chartData = json.monthly || [];
      this.market = (json.market && (json.market.adr != null || json.market.occupancy != null)) ? json.market : null;

      // Portfolio-level financial series per spanned calendar year:
      // fee-engine mgmt rev / owner net (projections summary) + AirROI
      // projected gross (liveness-prorated) + booked-by-this-date-LY.
      this.finByMonth = new Map();
      if (!this.selectedListingId) {
        const years = [...new Set([this.fromMonth, this.toMonth].map(s => Number(s.slice(0, 4))))];
        const span: number[] = [];
        for (let yy = years[0]; yy <= years[years.length - 1]; yy++) span.push(yy);
        await Promise.all(span.map(async (yy) => {
          const [sumR, projR] = await Promise.all([
            fetch(`/api/projections/summary/${yy}`).then(r => r.ok ? r.json() : null).catch(() => null),
            fetch(`/api/projections/airroi/${yy}?scenario=expected`).then(r => r.ok ? r.json() : null).catch(() => null),
          ]);
          for (let m = 0; m < 12; m++) {
            const key = `${yy}-${String(m + 1).padStart(2, '0')}`;
            const sm = sumR?.monthly?.[m];
            this.finByMonth.set(key, {
              mgmt_fee: sm?.actual_mgmt_fee ?? null,
              owner_net: sm?.actual_net ?? null,
              projected: projR?.available ? (projR.monthly?.[m]?.projected_gross ?? null) : null,
              stly: projR?.available ? (projR.stly_monthly?.[m] ?? null) : null,
            });
          }
        }));
      }
      if (!this.selectedListingId && json.listings?.length) {
        this.listings = json.listings;
        const select = document.getElementById('pmx-property') as HTMLSelectElement | null;
        if (select && select.options.length <= 1) {
          select.innerHTML = `<option value="">All Properties</option>` +
            this.listings.map(l => `<option value="${l.id}">${Formatters.escapeHtml(l.name)}</option>`).join('');
        }
      }
      this.draw();
    } catch (err) {
      console.error('Portfolio metrics chart failed:', err);
    }
  }

  private values(months: string[], metric: MetricKey): (number | null)[] {
    if (PORTFOLIO_ONLY.includes(metric)) {
      return months.map(m => this.finByMonth.get(m)?.[metric as 'mgmt_fee'] ?? null);
    }
    const map = new Map(this.chartData.map((d: any) => [d.month, d]));
    return months.map(m => {
      const d = map.get(m) as any;
      if (!d) return 0;
      if (metric === 'revenue') return d.revenue;
      if (metric === 'nights') return d.nights;
      if (metric === 'reservations') return d.count;
      if (metric === 'adr') return d.nights > 0 ? d.revenue / d.nights : 0;
      return 0;
    });
  }

  private draw(): void {
    const canvas = document.getElementById('pmx-canvas') as HTMLCanvasElement | null;
    if (!canvas) return;
    if (this.chart) { this.chart.destroy(); this.chart = null; }

    const [sy, sm] = this.fromMonth.split('-').map(Number);
    const [ey, em] = this.toMonth.split('-').map(Number);
    const months: string[] = [];
    for (let y = sy, m = sm; y < ey || (y === ey && m <= em); m++) {
      if (m > 12) { m = 1; y++; }
      months.push(`${y}-${String(m).padStart(2, '0')}`);
    }
    if (!months.length) return;

    const fmtMo = (key: string) => {
      const [y, mo] = key.split('-').map(Number);
      return new Date(y, mo - 1, 1).toLocaleString('default', { month: 'short', year: 'numeric' });
    };
    const rangeEl = document.getElementById('pmx-range');
    if (rangeEl) rangeEl.textContent = `${fmtMo(months[0])} – ${fmtMo(months[months.length - 1])}`;

    const labels = months.map(m => {
      const [y, mo] = m.split('-').map(Number);
      return new Date(y, mo - 1, 1).toLocaleString('default', { month: 'short', year: '2-digit' });
    });
    const dataA = this.values(months, this.metricA);
    const dataB = this.values(months, this.metricB);
    const isCurrencyA = CURRENCY.has(this.metricA);
    const isCurrencyB = CURRENCY.has(this.metricB);
    // Shared axis when both metrics live in the same unit/scale group.
    const sharedAxis = SCALE_GROUP[this.metricA] === SCALE_GROUP[this.metricB];
    const axisB = sharedAxis ? 'yA' : 'yB';

    const datasets: any[] = [
      { type: 'bar' as const, label: METRIC_LABELS[this.metricA], data: dataA, backgroundColor: 'rgb(31,41,55)', borderWidth: 0, borderRadius: 2, yAxisID: 'yA', order: 2 },
      { type: 'line' as const, label: METRIC_LABELS[this.metricB], data: dataB, borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,0.06)', borderWidth: 2, pointRadius: 2.5, pointBackgroundColor: '#2563eb', tension: 0.35, fill: true, yAxisID: axisB, order: 1, spanGaps: false, borderDash: (this.metricB === 'projected' || this.metricB === 'stly') ? [6, 4] : undefined },
    ];
    // Market ADR overlay — dashed reference on whichever axis shows ADR.
    if (this.market?.adr != null && (this.metricA === 'adr' || this.metricB === 'adr')) {
      const axis = this.metricA === 'adr' ? 'yA' : axisB;
      datasets.push({
        type: 'line' as const, label: 'Market ADR', data: months.map(() => this.market!.adr as number),
        borderColor: '#f59e0b', borderWidth: 2, borderDash: [6, 4], pointRadius: 0, fill: false, yAxisID: axis, order: 0,
      });
    }

    this.chart = new Chart(canvas, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'top', labels: { boxWidth: 12, font: { size: 11 } } },
          tooltip: {
            callbacks: {
              label: (ctx: any) => {
                const isCur = (ctx.datasetIndex === 0 ? isCurrencyA : isCurrencyB) || ctx.dataset.label === 'Market ADR';
                const v = ctx.parsed.y;
                return `${ctx.dataset.label}: ${isCur ? '$' + Math.round(v).toLocaleString() : Math.round(v).toLocaleString()}`;
              },
            },
          },
        },
        scales: {
          x: { ticks: { color: '#64748b', font: { size: 10 }, maxRotation: 0 }, grid: { color: 'rgba(100,116,139,0.08)' } },
          yA: { position: 'left', beginAtZero: true, ticks: { color: '#64748b', font: { size: 10 }, callback: (v: any) => isCurrencyA ? '$' + Number(v).toLocaleString() : Number(v).toLocaleString() }, grid: { color: 'rgba(100,116,139,0.12)' } },
          yB: { position: 'right', display: !sharedAxis, beginAtZero: true, ticks: { color: '#60a5fa', font: { size: 10 }, callback: (v: any) => isCurrencyB ? '$' + Number(v).toLocaleString() : Number(v).toLocaleString() }, grid: { display: false } },
        },
      },
    });
  }
}
